import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import StarAnimationWhite from '../assets/animations/StarAnimationWhite.json';
import SuccessModal from './SuccessModal';
import RewardStatus from '../components-library/rewards/RewardStatus';
import { ProductInterface } from '../services/exports/Interfaces';
import collect from 'collect.js';
import CompanyManager from '../services/api/CompanyManager';
import useLoyaltyProgram from '../hooks/loyalty/useLoyaltyProgram';
import { useTranslation } from 'react-i18next';
import ProductsCarousel from '../components-library/carousel/ProductsCarousel';
import useGoogleReviewSuggestedProducts from '../hooks/google-review/useGoogleReviewSuggestedProducts';
import useAppMethods from '../hooks/utility/useAppMethods';
import useRewardAccount from '../hooks/loyalty/useRewardAccount';
import { CompanyDetailsInterface } from '../services/exports/Interfaces';

interface Props {
  open: boolean;
  buttonTitle?: string;
  type?: 'welcome_to_loyalty_program' | 'first_point_earned';
  company: CompanyDetailsInterface;
  toggleModal: () => void;
  setLoading?: (loading: boolean) => void;
}

const PointsEarnedModal = forwardRef((props: Props, ref: any): JSX.Element => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:PointsEarnedModal' });

  const { open, buttonTitle, type, company, toggleModal, setLoading } = props;

  const { formatCurrency } = useAppMethods();
  const { maxReward } = useRewardAccount({
    company,
  });
  const { loyalty, reward, rewardThreshold } = useLoyaltyProgram({
    company,
  });
  const { suggestedProducts } = useGoogleReviewSuggestedProducts({ autoLoad: true });

  const [show, setShow] = useState<boolean>(false);
  const [discount, setDiscount] = useState<number>(0);
  const [freeProducts, setFreeProducts] = useState<ProductInterface[]>([]);

  const oneFreeReward: boolean = freeProducts?.length === 1;
  const multiFreeReward: boolean = freeProducts?.length > 1;
  const mostExpensiveFreeProduct = () => collect(freeProducts)?.first();

  useImperativeHandle(ref, () => ({
    show,
  }));

  useEffect(() => {
    if (open) {
      onDidFocus();
    } else {
      onDidBlur();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setLoading && setLoading(!show);
    } else {
      setLoading && setLoading(false);
    }
  }, [show, open]);

  function onDidFocus(): void {
    findUnlockedReward();
  }

  function onDidBlur(): void {
    setShow(false);
  }

  async function findUnlockedReward(): Promise<void> {
    if (!maxReward) {
      return;
    }

    if (maxReward.discount === 100) {
      return getFreeMenuItems();
    }

    setDiscount(maxReward.discount);
    setTimeout(() => setShow(true), 1000);
  }

  async function getFreeMenuItems() {
    const { response, success } = await CompanyManager.getFreeMenuItems(company?.id);

    setTimeout(() => setShow(true), 500);
    if (success) {
      setFreeProducts(response?.data?.data);
    }
  }

  const renderFirstTimePointsEarned = (): JSX.Element => {
    function getTitle(): string {
      // 1 Free item
      if (oneFreeReward) {
        return t('points_earned.titles.free_menu_item', {
          product: mostExpensiveFreeProduct()?.name,
        });
      }

      // >=2 Free items
      if (multiFreeReward) {
        return t('points_earned.titles.multiple_free_menu_item', {
          product: mostExpensiveFreeProduct()?.name,
        });
      }

      // No free item/ % discount
      return t('points_earned.titles.no_free_menu_item', {
        discount,
      });
    }

    return (
      <div>
        <h6 className="mb-md">{getTitle()}</h6>
        <div className="bg-white p-md rounded-lg shadow dark:shadow-none">
          <RewardStatus hideTitle hideDescription />
        </div>
      </div>
    );
  };

  const renderAnimationDescription = (): JSX.Element => {
    function getTitle(): string {
      switch (type) {
        case 'welcome_to_loyalty_program':
          return t('animation_description.order_here_earn_rewards');
        default:
          return t('animation_description.order_here_earn_points');
      }
    }

    function getDescription(): string | undefined {
      if (type === 'welcome_to_loyalty_program') {
        if (loyalty?.is_per_item) {
          return t('animation_description.menu_item_reward_threshold', {
            threshold: formatCurrency(rewardThreshold),
            count: reward,
          });
        }

        return t('animation_description.order_reward_threshold', {
          threshold: formatCurrency(rewardThreshold),
          count: reward,
        });
      }
    }

    return (
      <div>
        <h4 className=" text-white-100 text-center">{getTitle()}</h4>
        {getDescription() && <p className="text-center text-white-100">{getDescription()}</p>}
      </div>
    );
  };

  return (
    <SuccessModal
      open={show}
      animationDescription={renderAnimationDescription()}
      lottieProps={{
        animationData: StarAnimationWhite,
      }}
      buttonTitle={buttonTitle ?? t('button.okay')}
      onConfirm={toggleModal}
    >
      {renderFirstTimePointsEarned()}
      {suggestedProducts?.length > 0 && (
        <div className="mt-md">
          <ProductsCarousel products={suggestedProducts} />
        </div>
      )}
    </SuccessModal>
  );
});

export default PointsEarnedModal;
